import { useAppDispatch } from "@/redux/hooks";
import { clearPickupFlight, setPickupFlightNumber } from "@/redux/slices/clientBooking/clientBookingSlice";

/**
 * The Flight Details modal's two exits, resolved once.
 *
 * Two panels open the same modal — the Address step's flight row, and the disrupted Date & Time panel's
 * "Change flight" — and both must resolve it identically. The rules are subtle enough that a second copy
 * would drift, and they now live entirely in the slice: `setPickupFlightNumber` decides what a changed
 * number invalidates (via `isSameFlightNumber`, the shared rule), and "Not Flying" clears the flight while
 * deliberately keeping the booked time.
 */
export const useFlightCapture = () => {
  const dispatch = useAppDispatch();

  return {
    /**
     * Continue — commit the entered flight number.
     *
     * The BOOKED DATE SURVIVES a flight change (DEV-1510). This used to null `pickupDatetime` whenever the
     * number actually changed, which did force the Date & Time panel to re-derive against the new flight —
     * but it also sent the calendar back to today. A cancelled VA829 on the 31st, changed to QF1424, opened
     * the panel on the 28th, and a replacement that does not operate today then greeted the client with
     * "we couldn't find flight QF1424 on that date" — the successful change reading as a failed one.
     *
     * The pickup keeps the date the client chose; only the derivation re-runs. The re-derivation is now
     * triggered by `pickupFlightChangedSinceCommit`, which the reducer sets on the same comparison — see
     * that field for why the panel needs a signal at all.
     *
     * No comparison here: the reducer already makes it, with the shared `isSameFlightNumber`, and one
     * comparison is the point. A no-op re-confirm invalidates nothing.
     */
    confirmFlightNumber: (flightNumber: string) => dispatch(setPickupFlightNumber(flightNumber)),
    /** Not Flying — see `clearPickupFlight` for what goes and, more importantly, what stays. */
    notFlying: () => dispatch(clearPickupFlight()),
  };
};
